import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, AlertCircle, Calendar, MapPin, DollarSign, Clock, User, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const OfferAccept = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [offerDetails, setOfferDetails] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid offer link - token missing');
      setLoading(false);
      return;
    }

    verifyOffer();
  }, [token]);

  const verifyOffer = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/offers/verify?token=${token}`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to verify offer');
      }

      setOfferDetails(data.offer);
    } catch (err) {
      console.error('Verify offer error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const acceptOffer = async () => {
    try {
      setConfirming(true);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/offers/accept?token=${token}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to accept offer');
      }

      setSuccess(true);

      // Redirect to dashboard after 5 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 5000);

    } catch (err) {
      console.error('Accept offer error:', err);
      setError(err.message);
    } finally {
      setConfirming(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <Loader className="w-16 h-16 text-green-500 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Offer Details</h2>
            <p className="text-gray-600">Please wait while we retrieve your offer...</p>
          </div>
        )}

        {/* Confirmation State - Show offer details and ask for confirmation */}
        {offerDetails && !success && !error && !loading && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Space Grotesk' }}>
                Accept Job Offer?
              </h2>
              <p className="text-green-100 text-sm">
                Please review the details before accepting
              </p>
            </div>

            {/* Offer Details */}
            <div className="p-6 space-y-4">
              {/* Job Title */}
              <div className="text-center pb-4 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-800">{offerDetails.job_title}</h3>
                <p className="text-gray-600 text-sm">from {offerDetails.owner_name}</p>
              </div>

              {/* Job Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                  <Calendar className="w-5 h-5 text-purple-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Date</p>
                    <p className="text-sm font-semibold text-gray-800">{formatDate(offerDetails.job_date)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                  <Clock className="w-5 h-5 text-orange-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Duration</p>
                    <p className="text-sm font-semibold text-gray-800">{offerDetails.hours} hours</p>
                  </div>
                </div>
              </div>

              {offerDetails.job_location && (
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                  <MapPin className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Location</p>
                    <p className="text-sm font-semibold text-gray-800">{offerDetails.job_location}</p>
                  </div>
                </div>
              )}

              {/* Your Payout */}
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Your Payout</p>
                      <p className="text-sm text-gray-600">{formatCurrency(offerDetails.pay_rate)}/hr × {offerDetails.hours}hrs</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(offerDetails.crew_payout)}</p>
                  </div>
                </div>
              </div>

              {/* Time Remaining */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div className="text-sm">
                  <span className="font-semibold text-amber-900">Time remaining: </span>
                  <span className="text-amber-700">{offerDetails.hours_remaining}h {offerDetails.minutes_remaining}m</span>
                </div>
              </div>

              {/* Confirmation Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-gray-700 font-semibold mb-2">By accepting this offer:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• You commit to working this job on the scheduled date</li>
                  <li>• Payment will be held securely until job completion</li>
                  <li>• You'll receive the job owner's contact details</li>
                </ul>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-6 pt-0 space-y-3">
              <Button
                onClick={acceptOffer}
                disabled={confirming}
                className="w-full h-14 text-base font-bold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg rounded-xl"
              >
                {confirming ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Yes, Accept This Offer
                  </>
                )}
              </Button>
              <Button
                onClick={() => navigate(`/offer/decline?token=${token}`)}
                variant="outline"
                disabled={confirming}
                className="w-full h-12 text-sm font-medium border-gray-300 text-gray-600 hover:bg-gray-50 rounded-xl"
              >
                Decline Offer Instead
              </Button>
            </div>
          </div>
        )}

        {/* Success State */}
        {success && !loading && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-3">Offer Accepted!</h2>
            <p className="text-lg text-gray-600 mb-6">
              Congratulations! You've successfully accepted the job offer.
            </p>

            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Payment Secured</strong>
              </p>
              <p className="text-xs text-gray-600">
                The payment has been authorised and will be transferred to your account after job completion.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-700 font-semibold mb-2">What's Next?</p>
              <ul className="text-xs text-gray-600 text-left space-y-1">
                <li>• Check your email for confirmation details</li>
                <li>• Find this job in your Dashboard under "Upcoming Jobs"</li>
                <li>• Contact the job owner if you have questions</li>
              </ul>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Redirecting to dashboard in 5 seconds...
            </p>

            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-700"
            >
              Go to Dashboard Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Unable to Accept Offer</h2>
            <p className="text-gray-600 mb-6">{error}</p>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-700 font-semibold mb-2">Common Reasons:</p>
              <ul className="text-xs text-gray-600 text-left space-y-1">
                <li>• Offer has expired (48-hour limit)</li>
                <li>• Offer was already accepted or declined</li>
                <li>• Invalid or expired link</li>
              </ul>
            </div>

            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full h-12 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-semibold rounded-xl hover:from-gray-600 hover:to-gray-700"
            >
              Return to Dashboard
            </Button>
          </div>
        )}

      </div>
    </div>
  );
};

export default OfferAccept;
